import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppNotification } from '../models/notification';
import { NotificationsHttpService } from './http/notifications-http-service';

/**
 * Live notification feed, backed by the server's SSE endpoint.
 *
 * Four server behaviours shape this class:
 *
 * 1. The stream replays the **whole unread backlog** on every connect and never marks
 *    anything read, so the same notification arrives again after each reconnect. Everything
 *    is therefore keyed by id and deduped rather than appended.
 * 2. The server's sink is `directBestEffort`, which *drops* events for a slow subscriber
 *    instead of buffering, and is per-instance. A reconnect plus the unread replay is the
 *    only recovery path, so reconnecting matters more than it would with a durable queue.
 * 3. `EventSource` is not `HttpClient`, so it bypasses `authInterceptor` entirely — no
 *    bearer header, and no 401-triggered token refresh. Auth rides on the `access_token`
 *    cookie via `withCredentials`, which works because that cookie's path covers
 *    `/api/notifications` and `SameSite=Strict` still counts localhost:4200 -> :8080 as
 *    same-site. A 401 here cannot be retried, so it is treated as "stop trying".
 * 4. The stream **closes itself** after a bounded lifetime and emits a keep-alive comment
 *    frame every 15s in between, so it survives the idle timeout of the reverse proxy in
 *    front of the API. Two consequences: a close is routine rather than a fault, so it
 *    reconnects promptly instead of escalating the backoff; and because an open stream is
 *    never quiet for long, prolonged silence is a reliable signal that the socket is dead —
 *    which the watchdog acts on. `EventSource` cannot see comment frames (`onmessage` fires
 *    only for `data:`), so liveness is tracked through `onopen` plus a wall-clock check
 *    rather than by observing the heartbeats directly.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationStreamService implements OnDestroy {
  private static readonly url = `${environment.baseurl}/api/notifications`;
  private static readonly FIRST_RETRY_MS = 1_000;
  private static readonly MAX_RETRY_MS = 60_000;

  /**
   * Delay after the server ends the stream at the end of its configured lifetime. Kept short
   * and deliberately kept off the backoff curve: that close is routine, not a fault.
   */
  private static readonly PROMPT_RETRY_MS = 1_500;

  /**
   * Silence longer than this means the connection is dead rather than idle.
   *
   * Sized against the server's **stream lifetime** (5m), not its heartbeat interval (15s).
   * That distinction matters and is easy to get wrong: the heartbeats do keep the connection
   * warm end-to-end, but `EventSource` cannot see them — `onmessage` fires only for `data:`
   * frames, and comment frames are invisible to it. So from this class's point of view a
   * healthy but quiet stream produces nothing at all between `onopen` and the server closing
   * it. Anything under the stream lifetime here would kill working connections on a loop.
   *
   * The heartbeats are still what makes this endpoint usable — they stop the reverse proxy
   * closing an idle stream — the client just cannot observe them to prove liveness.
   */
  private static readonly STALE_AFTER_MS = 7 * 60_000;
  private static readonly WATCHDOG_PERIOD_MS = 30_000;

  /**
   * How long after `start()` arrivals are treated as pre-existing rather than new.
   *
   * Covers the history response plus the stream's backlog replay. Generous on purpose: a
   * popup wrongly suppressed is invisible, whereas a screenful of popups on every page load
   * is the kind of thing that gets a notification feature switched off.
   */
  private static readonly PRIMING_WINDOW_MS = 3_000;

  private readonly notificationsHttpService = inject(NotificationsHttpService);

  /** Keyed by id: the backlog replays on every reconnect and must not pile up. */
  private readonly byId = new Map<string, AppNotification>();
  private readonly notifications$ = new BehaviorSubject<AppNotification[]>([]);

  /**
   * Notifications seen for the first time, for a transient popup.
   *
   * Separate from {@link feed$} because that cannot tell an arrival from a replay: the server
   * resends the entire unread backlog on every reconnect, and the stream is re-established
   * every few minutes by design. Emitting per unseen id is what keeps a popup from firing
   * repeatedly for notifications the admin has already been shown.
   */
  private readonly received$ = new Subject<AppNotification>();

  /**
   * True until the first batch of notifications has been taken in.
   *
   * The history fetch and the stream's backlog replay both arrive right after `start()`, and
   * neither is news — popping toasts for a page load would bury the screen. Only what turns
   * up after that initial settle is treated as an arrival.
   */
  private priming = true;
  private primingTimer: ReturnType<typeof setTimeout> | null = null;

  private eventSource: EventSource | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelayMs = NotificationStreamService.FIRST_RETRY_MS;
  private started = false;

  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  /** Last time the current connection opened or delivered a notification. */
  private lastActivityAtMs = 0;
  /** Whether the current connection got as far as a response; reset on each connect. */
  private openedThisConnection = false;

  get feed$() {
    return this.notifications$.asObservable();
  }

  /**
   * Fires once per notification never seen before, and never during the initial load.
   * Safe to drive a popup from.
   */
  get arrivals$() {
    return this.received$.asObservable();
  }

  get unreadCount() {
    return this.notifications$.value.filter((n) => !n.isRead).length;
  }

  /** Idempotent: repeated calls (e.g. re-render of the bell) reuse the open connection. */
  start() {
    if (this.started) return;
    this.started = true;

    // Everything that lands in this window is pre-existing rather than new: the history page
    // and the stream's unread replay both arrive now. Timer-based because the two come from
    // independent responses, so there is no single "done" event to hang this off.
    this.priming = true;
    this.clearPriming();
    this.primingTimer = setTimeout(() => {
      this.priming = false;
      this.primingTimer = null;
    }, NotificationStreamService.PRIMING_WINDOW_MS);

    this.loadHistory();
    this.connect();
    this.startWatchdog();
  }

  stop() {
    this.started = false;
    this.clearRetry();
    this.stopWatchdog();
    this.clearPriming();
    // so a later start() (e.g. a new login in the same tab) primes again instead of toasting
    // the whole backlog
    this.priming = true;
    this.eventSource?.close();
    this.eventSource = null;
  }

  ngOnDestroy() {
    this.stop();
    this.received$.complete();
  }

  /**
   * Marks one notification read. Optimistic, because the endpoint returns 204 with no body
   * and the stream will not re-send the change.
   *
   * On a role broadcast this clears the notification for **every** holder of that role, by
   * design on the server. That is why nothing here marks on render — only an explicit user
   * action reaches this method.
   */
  markAsRead(notification: AppNotification) {
    if (notification.isRead) return;

    const previous = notification.isRead;
    this.patch(notification.id, { isRead: true });

    this.notificationsHttpService.markAsRead(notification.id).subscribe({
      error: () => this.patch(notification.id, { isRead: previous }),
    });
  }

  /**
   * Reflects a server-side clear locally.
   *
   * Accepting or rejecting a payment marks its admin notification read on the server, but
   * this client's copy is already in memory and the stream never re-sends a change to an
   * existing notification. Without this the bell would keep a stale unread badge until the
   * next reconnect.
   *
   * Local only — no request, because the server has already done the write.
   */
  markReadForReference(referenceId: string, type: string) {
    let changed = false;

    for (const notification of this.byId.values()) {
      if (
        notification.referenceId === referenceId &&
        notification.type === type &&
        // the rep's own notification for the same record is addressed to them by id, and
        // the server leaves it unread; mirror that here
        notification.recipientRole !== null &&
        !notification.isRead
      ) {
        this.byId.set(notification.id, { ...notification, isRead: true });
        changed = true;
      }
    }

    if (changed) this.publish();
  }

  /** Also shared for a role broadcast: this removes it for everyone in that role. */
  delete(notification: AppNotification) {
    const removed = this.byId.get(notification.id);
    this.byId.delete(notification.id);
    this.publish();

    this.notificationsHttpService.deleteNotification(notification.id).subscribe({
      error: () => {
        if (removed) {
          this.byId.set(removed.id, removed);
          this.publish();
        }
      },
    });
  }

  /**
   * Seeds the list from the paginated history so the bell shows read entries too — the
   * stream only replays unread ones. Also re-run after a reconnect, to recover anything the
   * sink dropped while this client was away.
   */
  private loadHistory() {
    this.notificationsHttpService.getNotifications({ page: 0, size: 30 }).subscribe({
      next: (page) => {
        for (const notification of page.content) this.merge(notification);
        this.publish();
      },
      // the bell still works off the stream alone; no need to surface this
      error: () => {},
    });
  }

  private connect() {
    if (!this.started) return;
    this.eventSource?.close();

    // withCredentials so the httpOnly access_token cookie is sent; there is no way to set
    // an Authorization header on an EventSource
    const source = new EventSource(NotificationStreamService.url, { withCredentials: true });
    this.eventSource = source;
    this.lastActivityAtMs = Date.now();
    this.openedThisConnection = false;

    // Fires once the response headers arrive. Counts as activity even though no notification
    // has been delivered yet, and marks the connection as having genuinely reached the server.
    source.onopen = () => {
      this.lastActivityAtMs = Date.now();
      this.openedThisConnection = true;
      this.retryDelayMs = NotificationStreamService.FIRST_RETRY_MS;
    };

    source.onmessage = (event) => {
      this.lastActivityAtMs = Date.now();
      this.retryDelayMs = NotificationStreamService.FIRST_RETRY_MS;
      try {
        this.merge(JSON.parse(event.data) as AppNotification);
        this.publish();
      } catch {
        // a frame we cannot parse is not worth tearing the stream down for
      }
    };

    source.onerror = () => {
      // EventSource reports network loss and a 401 identically, and it retries on its own
      // in some browsers; closing first keeps the backoff here the only retry path
      source.close();
      if (this.eventSource === source) this.eventSource = null;

      // A connection that opened successfully and then ended is almost always the server
      // closing it at the end of its configured lifetime — routine, and it happens every few
      // minutes. Retrying that on the escalating curve would drift to minute-long gaps during
      // normal operation and leave an admin unreachable for no reason. A connection that
      // never opened is a real failure and falls through to the backoff.
      this.scheduleReconnect(
        this.openedThisConnection ? NotificationStreamService.PROMPT_RETRY_MS : undefined,
      );
    };
  }

  /**
   * Reconnects a stream that has gone quiet.
   *
   * The server heartbeats every 15s, so silence on an open connection is unambiguous: the
   * socket is dead and simply has not reported it. `EventSource` will not notice by itself —
   * it has no read timeout — so without this check a black-holed connection looks exactly
   * like an idle healthy one and the bell stops updating with no error anywhere.
   */
  private startWatchdog() {
    this.stopWatchdog();
    this.watchdogTimer = setInterval(() => {
      if (!this.started || this.eventSource === null) return;
      if (Date.now() - this.lastActivityAtMs < NotificationStreamService.STALE_AFTER_MS) return;

      this.eventSource.close();
      this.eventSource = null;
      this.scheduleReconnect(NotificationStreamService.PROMPT_RETRY_MS);
    }, NotificationStreamService.WATCHDOG_PERIOD_MS);
  }

  private stopWatchdog() {
    if (this.watchdogTimer !== null) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  /**
   * Schedules the next connect attempt.
   *
   * Two cadences, because a closed stream means two different things. The server ends the
   * stream deliberately at the end of its lifetime, which is routine and gets `fixedDelayMs`
   * — no escalation, since escalating on the normal case would leave an admin waiting a
   * minute for a notification while nothing is actually wrong. A real failure falls through
   * to the capped exponential curve, so a server that is down or a session that has expired
   * does not get hammered.
   */
  private scheduleReconnect(fixedDelayMs?: number) {
    if (!this.started || this.retryTimer !== null) return;

    let delay: number;
    if (fixedDelayMs === undefined) {
      delay = this.retryDelayMs;
      this.retryDelayMs = Math.min(delay * 2, NotificationStreamService.MAX_RETRY_MS);
    } else {
      delay = fixedDelayMs;
    }

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.started) return;
      // the replay covers anything missed while disconnected, but the sink may also have
      // dropped events, so refetch the history as well
      this.loadHistory();
      this.connect();
    }, delay);
  }

  private clearRetry() {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.retryDelayMs = NotificationStreamService.FIRST_RETRY_MS;
  }

  /**
   * Last write wins, so a replayed copy refreshes a stale one. A locally-applied read flag
   * is kept, since the server's replay is unread-only and would otherwise revive an entry
   * the user just cleared.
   */
  private merge(notification: AppNotification) {
    const existing = this.byId.get(notification.id);
    this.byId.set(notification.id, {
      ...notification,
      isRead: existing?.isRead ? true : notification.isRead,
    });

    // An id not seen before, once past the initial load, is genuinely new. Already-read
    // entries are excluded: those only appear via the history fetch, and a popup for
    // something the admin has already dealt with is just noise.
    if (!existing && !this.priming && !notification.isRead) {
      this.received$.next(notification);
    }
  }

  private clearPriming() {
    if (this.primingTimer !== null) {
      clearTimeout(this.primingTimer);
      this.primingTimer = null;
    }
  }

  private patch(id: string, changes: Partial<AppNotification>) {
    const existing = this.byId.get(id);
    if (!existing) return;
    this.byId.set(id, { ...existing, ...changes });
    this.publish();
  }

  /** Newest first, matching the history endpoint's ordering. */
  private publish() {
    this.notifications$.next(
      [...this.byId.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    );
  }
}
